'use client';
import { Tooltip } from '@benzinga/core-ui';
import { formatRoundFixed } from '@benzinga/utils';
import ReportSection from '../../../../../../src/components/ReportSection';
import { RankingDetail } from '@benzinga/quotes-manager';
import { getColor } from '@benzinga/ticker-ui';
import { SectionPaywall } from './SectionPaywall';

interface StockWithRankings extends RankingDetail {
  symbol: string;
}

const rankingDescriptions = {
  growth:
    "Growth measures a stock's combined historical expansion in earnings and revenue across multiple time periods, with emphasis on both long-term trends and recent performance.",
  value:
    "Value is a percentile-ranked composite metric that evaluates a stock's relative worth by comparing its market price to fundamental measures of the company's assets, earnings, sales, and operating performance.",
  momentum:
    "Momentum measures a stock's relative strength based on its price movement patterns and volatility over multiple timeframes, ranked as a percentile against other stocks.",
  quality:
    "Quality is a composite ranking that evaluates a company's operational efficiency and financial health by analyzing historical profitability metrics and fundamental strength indicators on a percentile basis relative to peers.",
};

export const RankingsSection = ({ data, index }) => {
  const buildRankingsSection = (data: StockWithRankings) => {
    return {
      rankings: {
        growth: data?.growth ?? null,
        value: data?.value ?? null,
        momentum: data?.momentum ?? null,
        quality: data?.quality ?? null,
      },
      title: 'Benzinga Edge Rankings',
      type: 'rankings',
    };
  };

  const section = buildRankingsSection(data);

  return (
    <>
    <ReportSection sectionIndex={index} title={section.title}>
      <div className="bg-white p-6">
        <p className="mb-4">
          <em className="font-bold">Benzinga Edge</em> stock rankings give you four critical scores to help you identify
          the strongest and weakest stocks to buy and sell.
        </p>
        <div className="relative">
          {Object.keys(section.rankings).map(ranking => {
            const score = section.rankings[ranking];
            if (score && typeof score === 'number') {
              return (
                <div key={ranking} className="flex flex-row gap-4 items-center mb-2">
                  <div className="flex flex-col items-center justify-start font-bold text-lg">
                    <Tooltip content={`View the best ${ranking} stocks`}>
                      <a
                        href={`https://www.benzinga.com/screener/top-${ranking}-stocks`}
                        className="capitalize min-w-[100px] text-center text-bzblue-800"
                      >
                        {ranking}
                      </a>
                    </Tooltip>
                    <p className={getColor(score)}>{formatRoundFixed(score, 2)}</p>
                  </div>
                  <p className="text-sm">{rankingDescriptions[ranking]}</p>
                </div>
              );
            } else {
              return null;
            }
          })}
          <SectionPaywall section="rankings" symbol={data.symbol} />
        </div>
      </div>
    </ReportSection>
    </>
  );
};  
